'use client';

import 'BomdisoftComponents/account/user-avatar-menu/styles.css';
import { LogoutRoundedIcon } from 'BomdisoftComponents/icons/logout-rounded';
import { Menu, MenuDivider, MenuItem } from 'BomdisoftComponents/menu/index';
import { Tooltip, type TooltipPlacement } from 'BomdisoftComponents/tooltip/index';
import { type ReactNode, useId, useLayoutEffect, useRef, useState } from 'react';

export type UserAvatarMenuItem = {
  label: string;
  href?: string;
  /** Marks a row that leaves the product's domain (the account center,
   * billing): opens in a new tab with the trailing open-in-new glyph. */
  external?: boolean;
  icon?: ReactNode;
  onClick?: () => void;
  testId?: string;
};

export type UserAvatarMenuProps = {
  name: string | null;
  email: string;
  avatarUrl?: string | null;
  items?: UserAvatarMenuItem[];
  signOutLabel: string;
  onSignOut: () => void;
  menuLabel?: string;
  /**
   * The switcher rows (the companies a person works in), rendered between the
   * identity header and the destination items so the active group leads the
   * menu (approved `account-switcher` design).
   */
  accountsSection?: ReactNode;
  /**
   * Shows the person's name beside the avatar in the trigger, for hosts with
   * room for it (an expanded sidebar). A name too long for the row ends in an
   * ellipsis, and only then does the trigger's tooltip carry the full name.
   */
  showName?: boolean;
  /**
   * Side for the collapsed trigger's menu-label tooltip. Hosts whose trigger
   * sits against a viewport edge (a sidebar rail's foot) pick the side with
   * room so the bubble never lands on the avatar itself.
   */
  tooltipPlacement?: TooltipPlacement;
  testId?: string;
};

const initialsOf = (name: string | null, email: string): string => {
  const source = name?.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : source.slice(0, 2);
  return letters.toUpperCase();
};

export const UserAvatarMenu = ({ accountsSection, avatarUrl, email, items = [], menuLabel = 'Account', name, onSignOut, showName, signOutLabel, testId = 'user-avatar-menu', tooltipPlacement = 'bottom' }: UserAvatarMenuProps) => {
  const [open, setOpen] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const menuId = useId();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);

  const displayName = name?.trim() || email;

  useLayoutEffect(() => {
    const label = showName && displayName ? nameRef.current : null;
    const measure = () => setTruncated(label !== null && label.scrollWidth > label.clientWidth);
    measure();
    // A sidebar expands through a width transition, so the mount-time
    // measurement sees the mid-animation width; the observer settles the
    // verdict on the label's final box.
    if (!label || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(label);
    return () => observer.disconnect();
  }, [displayName, showName]);

  const avatar: ReactNode = avatarUrl ? <img alt='' className='bomdisoft-user-avatar-menu-avatar-image' height={32} src={avatarUrl} width={32} /> : <span className='bomdisoft-user-avatar-menu-initials'>{initialsOf(name, email)}</span>;

  return (
    <>
      <Tooltip placement={showName ? 'top' : tooltipPlacement} title={showName ? (truncated ? displayName : '') : menuLabel}>
        <button aria-controls={menuId} aria-expanded={open} aria-haspopup='menu' aria-label={menuLabel} className='bomdisoft-user-avatar-menu-trigger' data-testid={`${testId}-trigger`} data-with-name={showName ? '' : undefined} onClick={() => setOpen(value => !value)} ref={anchorRef} type='button'>
          {showName ? <span className='bomdisoft-user-avatar-menu-trigger-avatar'>{avatar}</span> : avatar}
          {showName ? (
            <span className='bomdisoft-user-avatar-menu-trigger-name' data-testid={`${testId}-name`} ref={nameRef}>
              {displayName}
            </span>
          ) : null}
        </button>
      </Tooltip>
      <Menu anchorRef={anchorRef} className='bomdisoft-user-avatar-menu-menu' id={menuId} onClose={() => setOpen(false)} open={open}>
        <div className='bomdisoft-user-avatar-menu-header' data-testid={`${testId}-header`}>
          <span className='bomdisoft-user-avatar-menu-header-avatar'>{avatar}</span>
          <span className='bomdisoft-user-avatar-menu-identity'>
            {name ? <span className='bomdisoft-user-avatar-menu-name'>{name}</span> : null}
            <span className='bomdisoft-user-avatar-menu-email'>{email}</span>
          </span>
        </div>
        <MenuDivider />
        {accountsSection ? (
          <>
            {accountsSection}
            <MenuDivider />
          </>
        ) : null}
        {items.map(item => (
          <MenuItem
            external={item.external}
            href={item.href}
            key={item.label}
            onClick={() => {
              setOpen(false);
              item.onClick?.();
            }}
            testId={item.testId}
          >
            <span aria-hidden='true' className='bomdisoft-user-avatar-menu-item-icon'>
              {item.icon}
            </span>
            {item.label}
          </MenuItem>
        ))}
        {items.length > 0 ? <MenuDivider /> : null}
        <MenuItem
          onClick={() => {
            setOpen(false);
            onSignOut();
          }}
          testId={`${testId}-sign-out`}
          variant='danger'
        >
          <span aria-hidden='true' className='bomdisoft-user-avatar-menu-item-icon'>
            <LogoutRoundedIcon />
          </span>
          {signOutLabel}
        </MenuItem>
      </Menu>
    </>
  );
};
